'use client'

import * as SeparatorPrimitive from '@radix-ui/react-separator'
import * as React from 'react'

import { cn } from '@/lib/utils'

type DividerSize = 'sm' | 'md' | 'lg'

const sizeToken: Record<DividerSize, string> = {
  sm: 'var(--surface-width-sm)',
  md: 'var(--surface-width-md)',
  lg: 'var(--surface-width-lg)',
}

type DividerProps = {
  orientation?: 'horizontal' | 'vertical'
  size?: DividerSize
  dashed?: boolean
  /**
   * `.br-divider.inverted` do DS (o "fundo-escuro" do UIKIT): troca o token
   * --border-color para --pure-0, para a linha aparecer sobre fundo escuro.
   */
  inverted?: boolean
}

function Divider({
  className,
  orientation = 'horizontal',
  size = 'sm',
  dashed = false,
  inverted = false,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & DividerProps) {
  const cssVars = {
    '--divider-size': sizeToken[size],
    '--divider-border-style': dashed ? 'dashed' : 'solid',
    // O DS inverte a linha trocando o token, não a propriedade.
    ...(inverted ? { '--border-color': 'var(--pure-0)' } : null),
    ...style,
  } as React.CSSProperties
  const isVertical = orientation === 'vertical'

  // Variante "content": linhas dos dois lados de um rótulo central
  // (.br-divider.content). As linhas vêm de ::before/::after.
  if (children != null) {
    return (
      <div
        role="separator"
        aria-orientation={orientation}
        data-slot="divider"
        className={cn(
          'flex items-center justify-center text-[var(--color)]',
          isVertical ? 'flex-col self-stretch' : 'w-full',
          "before:flex-1 before:content-[''] after:flex-1 after:content-['']",
          'before:[border-color:var(--border-color)] after:[border-color:var(--border-color)]',
          isVertical
            ? [
                // No DS a linha vertical usa a borda direita e as pseudo não têm
                // margem (`margin: 0` em `.vertical.content`).
                'before:[border-right-width:var(--divider-size)] after:[border-right-width:var(--divider-size)]',
                'before:[border-right-style:var(--divider-border-style)] after:[border-right-style:var(--divider-border-style)]',
                'before:m-0 after:m-0',
              ]
            : [
                'before:[border-top-width:var(--divider-size)] after:[border-top-width:var(--divider-size)]',
                'before:[border-top-style:var(--divider-border-style)] after:[border-top-style:var(--divider-border-style)]',
                'before:mr-[var(--spacing-scale-base)] after:ml-[var(--spacing-scale-base)]',
              ],
          className
        )}
        style={cssVars}
        {...props}
      >
        {children}
      </div>
    )
  }

  return (
    <SeparatorPrimitive.Root
      data-slot="divider"
      decorative
      orientation={orientation}
      className={cn(
        'shrink-0 border-0 [border-color:var(--border-color)]',
        isVertical
          ? 'h-full self-stretch [border-right-width:var(--divider-size)] [border-right-style:var(--divider-border-style)]'
          : 'w-full [border-top-width:var(--divider-size)] [border-top-style:var(--divider-border-style)]',
        className
      )}
      style={cssVars}
      {...props}
    />
  )
}

export { Divider }
